import { getSession, signIn } from 'next-auth/react'
import { getRuntimeApiUrl } from './runtimeConfig'

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

let signInRedirectPending = false

const ACT_AS_HEADER = 'X-Act-As-Vendor'
const VENDOR_API = '/api/vendor/'
let actingVendor: string | null = null

/** Internal demo users act as one invited vendor on the supplier portal. The backend ignores it for real supplier logins. */
export function setActingVendor(vendorCode: string | null) {
  actingVendor = vendorCode
}

function withActingVendor(endpoint: string, headers: Headers) {
  if (actingVendor && endpoint.startsWith(VENDOR_API)) headers.set(ACT_AS_HEADER, actingVendor)
}

export type ApiClientOptions = RequestInit & {
  /** Pass from useSession() — getSession() can miss accessToken with chunked cookies */
  accessToken?: string
}

async function apiClientFetch<T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const { accessToken: explicitToken, ...fetchOptions } = options
  const session = explicitToken ? null : await getSession()
  const token = explicitToken ?? session?.accessToken

  const headers = new Headers(fetchOptions.headers || {})

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  withActingVendor(endpoint, headers)

  if (session?.error === 'RefreshAccessTokenError') {
    if (!signInRedirectPending) {
      signInRedirectPending = true
      await signIn('keycloak')
    }
    throw new Error('Session expired. Please sign in again.')
  }

  const API_URL = await getRuntimeApiUrl()
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...fetchOptions, headers })

  if (response.status === 401) {
    if (!token) {
      if (!signInRedirectPending) {
        signInRedirectPending = true
        await signIn('keycloak', { callbackUrl: window.location.href })
      }
      throw new Error('Authentication required.')
    }
    throw new Error('Unauthorized — check your account permissions.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new Error(errorData.detail || 'An API error occurred.')
  }

  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

export const apiClient = {
  get: <T = unknown>(endpoint: string, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  post: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: ApiClientOptions
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  put: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: ApiClientOptions
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  patch: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: ApiClientOptions
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /** Multipart upload — Content-Type is left to the browser so the boundary is set. */
  postForm: <T = unknown>(
    endpoint: string,
    form: FormData,
    options?: ApiClientOptions
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'POST', body: form })
  },

  delete: <T = unknown>(endpoint: string, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

/** Authenticated binary download (e.g. spreadsheet templates). */
export async function apiClientDownload(endpoint: string): Promise<Blob> {
  const session = await getSession()
  const headers = new Headers()
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  withActingVendor(endpoint, headers)
  const API_URL = await getRuntimeApiUrl()
  const response = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, { headers })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(errorData.detail || 'Download failed.')
  }
  return response.blob()
}

export default apiClientFetch
